/* eslint-disable react-hooks/exhaustive-deps */
import { Dispatch, useEffect, useState } from "react";

import { GAMBLING_CONTROLS } from "../constants";
import * as actionType from "../context/actionType";
import { hasObjectBlankValue, SFCommunicationHelper } from "../helpers";
import { IAction, IStartupData } from "../interfaces";

/**
 * Custom hook to manage startup data and communicat to IB with the SFCommunicationHelper.
 * @param {Dispatch<IAction>} dispatch - The dispatch function to update the context state.
 */
const useIBStartupData = (dispatch: Dispatch<IAction>) => {
  const [startupData, setStartupData] = useState<IStartupData | null>(null);
  const sfCommunicationHelper = new SFCommunicationHelper();

  /**
   * Sets the height of the iFrame for IB.
   * @param {number} iFrameHeight - The height to set for the iFrame.
   */
  const setIBHeight = (iFrameHeight: number): void => {
    sfCommunicationHelper.setHeight(iFrameHeight);
  };

  /**
   * Dispatches the IS_LOADING action.
   * Calls the onStart method of SFCommunicationHelper.
   */
  const onStart = (): void => {
    dispatch({
      type: actionType.IS_LOADING,
      payload: true,
    });
    sfCommunicationHelper.onStart();
  };

  /**
   * Handles the "onStartCallbackEvent" event and updates the startup data in useState.
   * Sets the container configuration for the SFCommunicationHelper.
   * Sets the Title for the SFCommunicationHelper.
   * @param {any} event - The event object containing the startup data.
   */
  const handleOnStartCallbackEvent = (event: any): void => {
    sfCommunicationHelper.setTitle(GAMBLING_CONTROLS);
    sfCommunicationHelper.setContainerConfig({
      showBack: false,
      showCancel: false,
      focusMode: false,
    });
    setStartupData(event.detail as IStartupData);
  };

  /**
   * Adds the "onStartCallbackEvent" event listener on mount and removes it on unmount.
   */
  const addEventListeners = () => {
    window.addEventListener("onStartCallbackEvent", handleOnStartCallbackEvent);

    return () => {
      window.removeEventListener(
        "onStartCallbackEvent",
        handleOnStartCallbackEvent
      );
    };
  };

  /**
   * Handles the interval to check and set startupData OR error and dispatches the appropriate actions.
   */
  const handleInterval = () => {
    let secondsRemaining =
      window.configuration.ON_START_EVENT_OCCURANCE_LIMIT ?? 50;

    const interval = setInterval(() => {
      if (!hasObjectBlankValue(startupData)) {
        dispatch({
          type: actionType.STARTUP_DATA_SUCCESS,
          payload: startupData,
        });
        clearInterval(interval);
      } else {
        secondsRemaining--;
      }

      if (secondsRemaining === 0) {
        dispatch({
          type: actionType.SET_ERROR,
          payload: "Set StartupData failed",
        });
        clearInterval(interval);
      }
    }, window.configuration.ON_START_INTERVAL);

    return () => {
      clearInterval(interval);
    };
  };

  useEffect(addEventListeners, []);
  useEffect(handleInterval, [startupData]);

  return {
    setIBHeight,
    onStart,
  };
};

export default useIBStartupData;
